import { isLiveMallClub } from '@/constants/systemType'
import { isEmpty } from 'lodash-es'
import type { NextPage } from 'next'
import { GetServerSideProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { QueryClient, dehydrate } from 'react-query'

// Libs
import { fetchProductData, SkuItemInfoType } from '@/api/fetchProductData'
import { fetchCategoryMenu } from '@/api/fetchCategoryMenu'
import { useCategoryMenu } from '@/hooks/useCategoryMenu'
import { getProductSchemaOrg, toJsonLd } from '@/methods/handleSchemaOrg'

// Components
import ProductComponent from '@/components/pages/Product'
import { getCategoryBreadcrumbSchemaList } from '@/components/pages/Product/component/Breadcrumb'

// Constants
import { envConfigs } from '@/constants/config'
import { DEFAULT_LOCALE } from '@/constants/defaultLocale'
import { ROUTE_PATH } from '@/constants/routePath'
import { QUERY_KEY } from '@/constants/queryKey'

// Styles

// Variables

const dateOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
}

export interface ProductProps {
  entity: {
    extra: {
      internal: any
    }
    isShow: boolean
    productInfo: {
      categoryId: number
      minPrice: number
      maxPrice: number
      categoryName: string
      currency: string
      dummySalesVolume: number
      origPrice: number
      productId: number
      productDesc?: string
      productName: string
      images: string[]
      isHidden: boolean
    }
    skuItemInfo?: SkuItemInfoType[]
  }
}

const Product: NextPage = (props: any) => {
  const { pageProps } = props
  const { productData, isWebView }: { productData: ProductProps; isWebView: boolean } = pageProps

  const { t } = useTranslation()
  const router = useRouter()

  const currentLocale = router.locale === 'default' ? DEFAULT_LOCALE : router.locale

  const date = new Intl.DateTimeFormat(router.locale, dateOptions).format(new Date())

  const images = productData?.entity?.productInfo?.images
  const hasImage = images?.length > 0

  //若 productData?.entity?.productInfo?.productName 不存在 則顯示 TutorABC Shop

  const title = isLiveMallClub
    ? 'Live Mall Club'
    : t('Meta_Title_Product', { productName: productData?.entity?.productInfo?.productName, date, brandName: 'TutorABC Shop' }) || 'TutorABC Shop'
  const price = productData?.entity?.productInfo?.minPrice || 0
  // 若多張則取前三張
  const imageUrls = hasImage ? images.slice(0, 3).map((image) => `${envConfigs.domainUrl}/api/og/product?image=${image}`) : ['/images/og-default.png']
  const ogUrl = `${envConfigs.domainUrl}/${currentLocale}${router.asPath}`

  const description = isLiveMallClub
    ? 'Live Mall Club'
    : productData?.entity?.productInfo?.productDesc || productData?.entity?.productInfo?.productName || 'TutorABC Shop'

  const productInfo = productData?.entity?.productInfo || {}
  const currency = productData?.entity?.productInfo?.currency || ''

  const { minPrice, maxPrice } = productInfo

  const skuItems = productData?.entity?.skuItemInfo ?? []
  const hasStock = skuItems.some((sku) => sku.stockQuantity > 0 && sku.status === 100)
  const availability = hasStock ? 'InStock' : 'OutOfStock'
  const mainImage = hasImage ? images[0] : `${envConfigs.domainUrl}/images/og-default.png`

  // 分類樹由 getServerSideProps 先塞進快取，SSR 就拿得到；跟畫面上的麵包屑同一份路徑
  const { data: categoryMenuData } = useCategoryMenu()
  const breadCrumbList = getCategoryBreadcrumbSchemaList({
    categoryTree: categoryMenuData?.root?.children,
    categoryId: productData?.entity?.productInfo?.categoryId,
    categoryName: productData?.entity?.productInfo?.categoryName,
    mallLabel: t('Header_Mall'),
    mallUrl: `${envConfigs.domainUrl}/${currentLocale}${ROUTE_PATH.MALL}`,
  })

  const schema = getProductSchemaOrg({
    type: 'Product',
    name: productData?.entity?.productInfo?.productName,
    description: productData?.entity?.productInfo?.productDesc || productData?.entity?.productInfo?.productName,
    image: mainImage,
    productId: productData?.entity?.productInfo?.productId,
    lowPrice: minPrice,
    highPrice: maxPrice,
    price: minPrice,
    availability: availability,
    url: ogUrl,
    breadCrumbList,
  })
  const isHidden = productData?.entity?.productInfo?.isHidden

  return (
    <>
      <Head>
        <title>{title}</title>
        <link rel='canonical' href={`${envConfigs.domainUrl}/${currentLocale}${ROUTE_PATH.PRODUCT}/${productData?.entity?.productInfo?.productId}`} />
        <meta name='title' key='meta-title' content={title} />
        <meta name='description' key='meta-description' content={description} />
        <meta property='product:price:amount' key='product-price-amount' content={`${price}`} />
        <meta property='product:price:currency' key='product-price-currency' content={currency} />
        <meta property='og:title' key='og-title' content={title} />
        <meta property='og:description' key='description' content={description} />
        <meta property='og:type' key='type' content='website' />
        <meta property='og:url' key='og-url' content={ogUrl} />
        {!isEmpty(imageUrls) && imageUrls.length > 0 && <meta property='og:image' key='og:image' content={imageUrls[0]} />}
        <meta property='og:price:amount' key='og-price-amount' content={`${price}`} />
        <meta property='og:price:currency' key='og-price-currency' content={currency} />
        <meta property='og:url' content={ogUrl} />
        {imageUrls.map((url, index) => (
          <meta property='og:image' key={`image${index}`} content={url} />
        ))}
        <meta property='og:price:amount' content={`${price}`} />
        <meta property='og:price:currency' content='TWD' />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='628' />
        {isHidden && <meta name='robots' content='noindex,nofollow' />}

        {/* 隱藏商品不輸出 Product 結構化資料：Merchant Center 的 automatic item updates 會爬這段，
            noindex 擋得住搜尋結果、擋不住商品資料被重新抓取 */}
        {!isLiveMallClub && !isHidden && <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: toJsonLd(schema) }} />}
      </Head>
      <ProductComponent productDataSSR={productData} isWebView={isWebView} />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ params, locale }) => {
  const { id }: any = params
  let result = null
  const menuLocale = locale === 'default' ? DEFAULT_LOCALE : locale
  const queryClient = new QueryClient()

  // 分類樹跟商品一起抓（麵包屑與 JSON-LD 用），失敗不影響商品頁，client 端的 useCategoryMenu 會再補抓
  const [productResult, categoryMenuResult] = await Promise.allSettled([
    fetchProductData({ productId: Number(id) }),
    fetchCategoryMenu({ ssrLocale: menuLocale, params: {} }),
  ])

  if (productResult.status === 'fulfilled') {
    result = productResult.value
  } else {
    console.log('fetchProductData error:', productResult.reason)
  }

  if (categoryMenuResult.status === 'fulfilled' && categoryMenuResult.value) {
    queryClient.setQueryData([QUERY_KEY.FETCH_CATEGORY_MENU, { locale: menuLocale }], categoryMenuResult.value)
  }

  return {
    props: { productData: result, dehydratedState: dehydrate(queryClient), ...(await serverSideTranslations(locale as string, ['common'])) },
  }
}

export default Product
